import { useState, useEffect, type CSSProperties, type ReactNode } from 'react';
import { Container, Group, NakedButton as PennyNakedButton, Text } from '@melio/penny';

import { useApp, Banner, Mono, NakedButton, Button, Card, Pill, Icon, Input, Field } from '@/components/ds';
import { Method, CodeBlock, CopyChip, Toggle, Segmented } from '@/components/code';
import { jsonStr } from '@/lib/codegen';
import { RES, DASH } from '@/data';
import { PageTitle, Table, Drawer } from '@/containers/dashboard';
import { DefList } from '@/containers/dashboard-businesses';
import { ApiKeyPanel } from '@/containers/onboarding';
import { PreviewNote, PreviewBadge, useAsync, Loading, ErrorBanner } from '@/components/live';
import {
  listKeys,
  rotateKey,
  getWebhook,
  patchWebhook,
  deleteWebhook,
  listWebhookEvents,
  rotateSigningSecret,
  type ApiKeyMeta,
  type OnboardEnv,
  type RotatedKey,
  type SigningSecret,
  type WebhookEventName,
} from '@/api/portal';

// dashboard-developers.tsx — Developers: API keys, Webhooks (both live), plus Logs & Events (preview).
// API keys and the webhook endpoint are both managed through the developers-portal backend, authenticated
// with the signed-in user's access token.

// "5h 20m left" / "42m left" / "under a minute left", for a key that is still working through its overlap.
const fmtTimeLeft = (s: string) => {
  const mins = Math.max(0, Math.floor((new Date(s).getTime() - Date.now()) / 60_000));
  if (isNaN(mins)) return '';
  if (mins < 1) return 'under a minute left';
  const h = Math.floor(mins / 60);
  return `${h > 0 ? `${h}h ` : ''}${mins % 60}m left`;
};

// The three columns every key card and history row shares, so their dates line up.
const KEY_FACTS: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 16, alignItems: 'start' };

function KeyFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Text textStyle="body4" color="semantic.text.secondary">
        {label}
      </Text>
      <Text as="div" textStyle="body3Semi">
        {children}
      </Text>
    </div>
  );
}

// ====== API KEYS =========================================================
// Live api-key management for the signed-in partner, per env (test = staging01, live = production).
// Listing returns metadata only; a secret is shown solely once, when freshly rotated. A partner has one key:
// rotating mints the new one and the previous one keeps working for 24h, during which only "rotate now" is offered.
function KeysView() {
  const app = useApp();
  const env: OnboardEnv = app.isLive ? 'production' : 'staging01';
  // Note: production key management is gated at the dashboard shell (ProductionAccessPending) when the
  // partner isn't operational, so this view only mounts when keys are manageable. The production `/keys`
  // routes also enforce it server-side (403 PARTNER_NOT_OPERATIONAL) as the authoritative check.

  const [keys, setKeys] = useState<ApiKeyMeta[]>([]);
  const [sonarKey, setSonarKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false); // the list loaded, so an empty one really means "no key" (not a failed load)
  const [error, setError] = useState('');
  const [revealOpen, setRevealOpen] = useState(false); // drawer showing the just-rotated key is open
  const [revealed, setRevealed] = useState<string | null>(null); // the just-rotated key, shown once
  const [previousKeyUntil, setPreviousKeyUntil] = useState<string | null>(null); // set while the reveal drawer shows a just-rotated key
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotateMode, setRotateMode] = useState<RotateMode>('overlap');
  const [rotateError, setRotateError] = useState(''); // the rotate drawer's own failure, shown only in the drawer
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false); // the expired keys are listed
  // One overlap at a time: while the previous key is still draining, only "rotate now" is offered (the API 409s an
  // overlap rotation too), so a key that leaked mid-overlap can still be cut off.
  const draining = keys.find((key) => key.status === 'draining');
  // Onboarding creates no key (a key is shown only when it's created), so a new partner starts here with none.
  const liveKeys = keys.filter((key) => key.status !== 'expired');
  const hasLiveKey = liveKeys.length > 0;
  const history = keys.filter((key) => key.status === 'expired');
  // The newest working key is the one in use and the one rotation replaces. Any other live key is the draining one, or an older
  // key a failed retirement left working with no end date.
  const current = liveKeys.find((key) => key.status === 'active') ?? liveKeys[0];
  const others = liveKeys.filter((key) => key !== current);
  const keyValue = (k: ApiKeyMeta) => (
    <Group variant="horizontal" spacing="s" alignItems="center">
      <Icon name="lock" size={16} color="var(--neutral-600)" />
      <Mono size="large">{k.maskedKey ?? `melio_sk_${app.isLive ? 'live' : 'test'}_••••••••`}</Mono>
    </Group>
  );
  // A live key: its value and status on top, then Created / Works until / Time left. The draining key is shaded, since it is on
  // its way out; an older key a failed retirement left working with no end date is flagged in red, as it should not be there.
  const liveCard = (k: ApiKeyMeta) => {
    const isDraining = k.status === 'draining';
    const stray = k !== current && k.status === 'active';
    return (
      <Card key={k.id} padding={20} style={{ marginBottom: 12, background: k === current ? undefined : 'var(--neutral-100)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          {keyValue(k)}
          <Pill kind={isDraining ? 'warning' : stray ? 'critical' : 'success'}>
            {isDraining ? 'Rotating out' : stray ? 'Needs attention' : 'Active'}
          </Pill>
        </div>
        <div style={{ ...KEY_FACTS, marginTop: 16 }}>
          <KeyFact label="Created">{fmtDateTime(k.createdAt)}</KeyFact>
          <KeyFact label="Works until">{isDraining ? fmtDateTime(k.expiredAt!) : 'Until you rotate it'}</KeyFact>
          <KeyFact label="Time left">{isDraining ? fmtTimeLeft(k.expiredAt!).replace(/ left$/, '') : '—'}</KeyFact>
        </div>
        {stray && (
          <Banner kind="critical" style={{ marginTop: 16 }}>
            This older key is still working and has no end date. Your next rotation retires it, or rotate now to stop it immediately.
          </Banner>
        )}
      </Card>
    );
  };

  const load = () => {
    setLoading(true);
    setError('');
    app
      .getAccessToken()
      .then((token) => (token ? listKeys(token, env) : Promise.reject(new Error('Your session has expired. Please sign in again.'))))
      .then(
        (list) => {
          setKeys(list.keys);
          setSonarKey(list.sonarKey);
          setLoaded(true);
          setLoading(false);
        },
        (e: Error) => {
          setError(e?.message || 'Could not load keys.');
          setLoaded(false);
          setLoading(false);
        }
      );
  };
  useEffect(load, [env]);

  const run = async (fn: (token: string) => Promise<void>, fallback: string) => {
    setBusy(true);
    setError('');
    try {
      const token = await app.getAccessToken();
      if (!token) throw new Error('Your session has expired. Please sign in again.');
      await fn(token);
    } catch (e: any) {
      setError(e?.message || fallback);
    } finally {
      setBusy(false);
    }
  };

  const closeReveal = () => {
    setRevealOpen(false);
    setRevealed(null);
    setPreviousKeyUntil(null);
  };
  const openRotate = () => {
    setRotateMode(draining ? 'now' : 'overlap');
    setRotateError('');
    setRotateOpen(true);
  };
  const doRotate = async () => {
    setBusy(true);
    setRotateError('');
    try {
      const token = await app.getAccessToken();
      if (!token) throw new Error('Your session has expired. Please sign in again.');
      const rotated: RotatedKey = await rotateKey(token, env, rotateMode === 'now');
      setRotateOpen(false);
      setRevealed(rotated.apiKey);
      setPreviousKeyUntil(rotated.previousKeyUntil ?? null);
      setRevealOpen(true);
      load();
    } catch (e: any) {
      setRotateError(e?.message || 'Could not rotate the key.');
    } finally {
      setBusy(false);
    }
  };

  // The first key: nothing to replace, so no dialog — create it and show it once.
  const createKey = () =>
    run(async (token) => {
      const created: RotatedKey = await rotateKey(token, env, false);
      setRevealed(created.apiKey);
      setPreviousKeyUntil(null);
      setRevealOpen(true);
      load();
    }, 'Could not create the key.');

  return (
    <div>
      <PageTitle title="API keys" sub="Secret keys authenticate your requests. Keep them server-side." />
      <Banner kind="brand" style={{ marginBottom: 20 }}>
        Managing keys for <strong>{app.isLive ? 'Live (production)' : 'Test (staging)'}</strong>. Your key is shown only when it is created.
        Rotate it to replace it — the previous key keeps working for 24 hours.
      </Banner>
      {/* Sonar publishable key: one value per partner+env (not per secret key), so it's shown once here
          rather than repeated on every row — unlike the api-keys above it, it's non-secret and safe to
          ship into the partner's own browser code to initialize the Sonar SDK client-side. Copy sits
          next to the key itself, not up in the header bar next to the "Sonar key" title. */}
      {sonarKey && <CodeBlock title="Sonar key" code={sonarKey} dark={false} copyPlacement="body" style={{ marginBottom: 20 }} />}
      {error && (
        <Banner kind="critical" style={{ marginBottom: 16 }}>
          {error}
        </Banner>
      )}
      {loaded && !loading && !hasLiveKey && (
        <Card padding={24} style={{ marginBottom: keys.length > 0 ? 20 : 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center' }}>
            <Icon name="lock" size={24} color="var(--neutral-600)" />
            <Text as="h3" textStyle="body2Semi">
              {keys.length > 0 ? 'You have no active API key' : 'You don’t have an API key yet'}
            </Text>
            <Text textStyle="body3" color="semantic.text.secondary">
              Create a key to authenticate requests to the {app.isLive ? 'live (production)' : 'test (staging)'} API. It’s shown only once,
              so copy it somewhere safe.
            </Text>
            <div style={{ marginTop: 8 }}>
              <Button onClick={createKey} loading={busy}>
                Create key
              </Button>
            </div>
          </div>
        </Card>
      )}
      {loading ? (
        <Loading label="Loading keys…" />
      ) : (
        <>
          {/* live keys: the one in use, plus (at most) the previous one still draining after a rotation. Every card and
              history row lays its facts out in the same three columns, so the dates line up down the page. */}
          {current && liveCard(current)}
          {others.map((k) => liveCard(k))}

          {/* expired keys: history only, so just their dates — there is no value left to show */}
          {history.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {liveKeys.length > 0 && (
                <NakedButton leftIcon={historyOpen ? 'chevron-down' : 'chevron-right'} onClick={() => setHistoryOpen((open) => !open)}>
                  {`${historyOpen ? 'Hide' : 'Show'} previous keys (${history.length})`}
                </NakedButton>
              )}
              {(historyOpen || liveKeys.length === 0) && (
                <Card padding={0} style={{ marginTop: 8 }}>
                  {history.map((k, i) => (
                    <div
                      key={k.id}
                      style={{ ...KEY_FACTS, padding: '12px 20px', borderTop: i === 0 ? undefined : '1px solid var(--neutral-200)' }}
                    >
                      <KeyFact label="Created">{fmtDateTime(k.createdAt)}</KeyFact>
                      <KeyFact label="Expired on">{fmtDateTime(k.expiredAt!)}</KeyFact>
                      <KeyFact label="Status">
                        <Pill kind="neutral">Expired</Pill>
                      </KeyFact>
                    </div>
                  ))}
                </Card>
              )}
            </div>
          )}
        </>
      )}

      {/* danger zone: rotating cuts the current key off, so it sits apart from the key list */}
      {loaded && hasLiveKey && (
        <>
          <Container paddingTop="l" paddingBottom="s">
            <Text as="h3" textStyle="heading3Semi">
              Danger zone
            </Text>
          </Container>
          <Card padding={16} style={{ border: '1px solid var(--critical-700)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Text textStyle="body2Semi">Rotate API key</Text>
                <Text textStyle="body4" color="semantic.text.secondary">
                  Creates a new key and replaces the current one. Your integration must switch to the new key; the current one keeps working
                  for 24 hours, or stops right away if you rotate now.
                </Text>
                {draining && (
                  <Text textStyle="body4" color="semantic.text.secondary">
                    A rotation is in progress, so only rotating now is available until the previous key stops working at{' '}
                    {fmtDateTime(draining.expiredAt!)}.
                  </Text>
                )}
              </div>
              <Button variant="critical" onClick={openRotate} disabled={loading}>
                Rotate key
              </Button>
            </div>
          </Card>
        </>
      )}

      {/* reveal / newly created key — the drawer already supplies the "API key" header and the
          footer, so this shows the key panel alone rather than onboarding's badge-and-heading
          treatment, and puts Done in the footer like every other drawer here. */}
      <Drawer
        open={revealOpen}
        onClose={closeReveal}
        title="API key"
        footer={revealed ? <Button onClick={closeReveal}>Done</Button> : undefined}
      >
        {revealed && (
          <>
            {previousKeyUntil && (
              <Banner kind="informative" style={{ marginBottom: 16 }}>
                Your previous key still works until <strong>{fmtDateTime(previousKeyUntil)}</strong>. Move your integration to this key
                before then.
              </Banner>
            )}
            <ApiKeyPanel apiKey={revealed} />
          </>
        )}
      </Drawer>

      {/* rotate */}
      <Drawer
        open={rotateOpen}
        onClose={() => setRotateOpen(false)}
        title="Rotate API key"
        footer={
          <>
            <Button variant="tertiary" onClick={() => setRotateOpen(false)}>
              Cancel
            </Button>
            <Button variant={rotateMode === 'now' ? 'critical' : 'primary'} loading={busy} onClick={doRotate}>
              Rotate key
            </Button>
          </>
        }
      >
        {rotateError && (
          <Banner kind="critical" style={{ marginBottom: 16 }}>
            {rotateError}
          </Banner>
        )}
        {draining ? (
          <Banner kind="informative" style={{ marginBottom: 16 }}>
            Your previous key still works until <strong>{fmtDateTime(draining.expiredAt!)}</strong>. Until then you can only rotate now —
            the 24-hour overlap is available again after that.
          </Banner>
        ) : (
          <div style={{ marginBottom: 16 }}>
            <Segmented
              value={rotateMode}
              onChange={(v) => setRotateMode(v as RotateMode)}
              options={[
                { value: 'overlap', label: 'Keep the old key valid for 24h' },
                { value: 'now', label: 'Rotate now' },
              ]}
            />
          </div>
        )}
        {rotateMode === 'overlap' ? (
          <Text textStyle="body2" color="semantic.text.secondary">
            This generates a new key for <strong>{app.isLive ? 'Live (production)' : 'Test (staging)'}</strong>. For the next 24 hours both
            the old and the new key authenticate requests, so you can move your integration over without downtime. After that, only the new
            key works.
          </Text>
        ) : (
          <Banner kind="critical">
            The current key stops working immediately, and requests using it fail until your integration has the new key. Use this if the
            key has leaked.
          </Banner>
        )}
      </Drawer>
    </div>
  );
}

// ====== LOGS =============================================================
function statusColor(code: number): string {
  return code < 300 ? 'success' : code < 400 ? 'informative' : code < 500 ? 'warning' : 'critical';
}

interface LogDetailProps {
  log: any;
  style?: CSSProperties;
}
function LogDetail({ log }: LogDetailProps) {
  const ok = log.status < 400;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Method m={log.method} />
        <Mono style={{ wordBreak: 'break-all' }}>{log.path}</Mono>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20, color: 'var(--text-secondary)' }}>
        <Mono>● {log.status}</Mono>
        <span>{log.ms} ms</span>
        <Mono>{log.id}</Mono>
        <span>{log.time}</span>
      </div>
      <Container style={{ marginBottom: 8 }}>
        <Text textStyle="caption1SemiUpper" color="semantic.text.secondary">
          Request body
        </Text>
      </Container>
      <CodeBlock
        dark
        code={jsonStr(
          log.method === 'POST' && log.path.includes('payments')
            ? { amount: 125000, originatingAccountId: 'acct_9a2f7', receivingAccountId: 'acct_7c4b1', deductionDate: '2026-06-10' }
            : {}
        )}
        maxHeight={160}
      />
      <Container style={{ margin: '16px 0 8px' }}>
        <Text textStyle="caption1SemiUpper" color="semantic.text.secondary">
          Response
        </Text>
      </Container>
      <CodeBlock dark code={jsonStr(ok ? RES.payment : RES.paymentEligibilityError)} maxHeight={220} />
    </div>
  );
}

function LogsView() {
  const [detail, setDetail] = useState<any | null>(null);
  return (
    <div>
      <PageTitle
        title={
          <>
            Logs <PreviewBadge />
          </>
        }
        sub="Every API request, with latency and request id. Reachable from any key."
      />
      <PreviewNote>A request-log inspector needs a partner-facing log store the backend doesn't have yet.</PreviewNote>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ flex: 1, maxWidth: 320 }}>
          <Input prefix="" placeholder="Filter by path, status, or request id" />
        </div>
        <Button variant="tertiary" leftIcon="filter" size="small">
          Filters
        </Button>
      </div>
      <Table
        onRow={(r) => setDetail(r)}
        columns={[
          { key: 'method', label: 'Method', render: (r) => <Method m={r.method} /> },
          {
            key: 'path',
            label: 'Path',
            render: (r) => <Mono size="caption">{r.path}</Mono>,
          },
          {
            key: 'status',
            label: 'Status',
            render: (r) => (
              <Mono bold style={{ color: `var(--${statusColor(r.status)}-700)` }}>
                {r.status}
              </Mono>
            ),
          },
          {
            key: 'ms',
            label: 'Latency',
            align: 'right',
            render: (r) => <Mono>{r.ms} ms</Mono>,
          },
          {
            key: 'time',
            label: 'Time',
            align: 'right',
            render: (r) => (
              <Mono size="caption" color="semantic.text.secondary">
                {r.time}
              </Mono>
            ),
          },
          {
            key: 'id',
            label: 'Request id',
            align: 'right',
            render: (r) => (
              <Mono size="caption" color="semantic.text.secondary">
                {r.id}
              </Mono>
            ),
          },
        ]}
        rows={DASH.logs}
      />
      <Drawer open={!!detail} onClose={() => setDetail(null)} title="Request log">
        {detail && <LogDetail log={detail} />}
      </Drawer>
    </div>
  );
}

// ====== EVENTS ===========================================================
interface EventDetailProps {
  evt: any;
  style?: CSSProperties;
}
function EventDetail({ evt }: EventDetailProps) {
  const ok = evt.code < 400;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Mono size="large" bold>
          {evt.type}
        </Mono>
        <Pill kind={ok ? 'success' : 'critical'}>{ok ? 'Delivered' : 'Failed'}</Pill>
      </div>
      <Card padding={18} style={{ marginBottom: 16 }}>
        <DefList
          rows={[
            ['Event id', evt.id],
            ['Related payment', evt.payment],
            ['Your endpoint', 'https://api.partner.co/webhooks/melio'],
            ['Response code', String(evt.code)],
            ['Attempts', String(evt.attempts)],
            ['Delivered at', evt.time],
          ]}
        />
      </Card>
      <Container style={{ marginBottom: 8 }}>
        <Text textStyle="caption1SemiUpper" color="semantic.text.secondary">
          Payload
        </Text>
      </Container>
      <CodeBlock
        dark
        code={jsonStr({
          id: evt.id,
          type: evt.type,
          created: '2026-05-28T09:31:48Z',
          data: { object: 'payment', id: evt.payment, status: evt.type.split('.')[1] },
        })}
        maxHeight={240}
      />
    </div>
  );
}

function EventsView() {
  const [detail, setDetail] = useState<any | null>(null);
  const [retried, setRetried] = useState<Record<string, boolean>>({});
  return (
    <div>
      <PageTitle
        title={
          <>
            Events <PreviewBadge />
          </>
        }
        sub="Webhook events with delivery status. Inspect payloads and re-trigger any event."
      />
      <PreviewNote>Events depend on payment-status webhooks, which aren't in scope yet.</PreviewNote>
      <Table
        onRow={(r) => setDetail(r)}
        columns={[
          {
            key: 'type',
            label: 'Event',
            render: (r) => <Mono bold>{r.type}</Mono>,
          },
          {
            key: 'payment',
            label: 'Resource',
            render: (r) => (
              <Mono size="caption" color="semantic.text.secondary">
                {r.payment}
              </Mono>
            ),
          },
          {
            key: 'status',
            label: 'Delivery',
            render: (r) => (
              <Pill kind={r.code < 400 ? 'success' : 'critical'}>{r.code < 400 ? 'Delivered' : `Failed · ${r.attempts}×`}</Pill>
            ),
          },
          {
            key: 'time',
            label: 'Time',
            align: 'right',
            render: (r) => (
              <Mono size="caption" color="semantic.text.secondary">
                {r.time}
              </Mono>
            ),
          },
          {
            key: 'x',
            label: '',
            align: 'right',
            render: (r) => (
              <NakedButton
                color="var(--accent)"
                onClick={(e) => {
                  e?.stopPropagation?.();
                  setRetried((s) => ({ ...s, [r.id]: true }));
                }}
              >
                {retried[r.id] ? 'Re-sent ✓' : 'Re-trigger'}
              </NakedButton>
            ),
          },
        ]}
        rows={DASH.events}
      />
      <Drawer open={!!detail} onClose={() => setDetail(null)} title="Event">
        {detail && <EventDetail evt={detail} />}
      </Drawer>
    </div>
  );
}

// ====== WEBHOOKS =========================================================
// Live against this env's portal API (`/webhook`, a single per-partner endpoint) authenticated with the
// signed-in user's access token. GET the endpoint, PATCH to create/update it (url, events, active state), DELETE to
// remove it. Deliveries are signed with the endpoint's signing secret (`/webhook/signing-secret`), not an API key,
// so revealing and rotating it lives here too.
const FALLBACK_EVENTS: WebhookEventName[] = [
  'api.entity.created',
  'api.entity.updated',
  'api.account.created',
  'api.account.updated',
  'api.account.deleted',
  'api.payment.created',
  'api.payment.updated',
  'api.limitation.updated',
];

const isHttpsUrl = (value: string): boolean => {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

const fmtDateTime = (s: string) => {
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleString();
};

const VERIFY_WITH_LIBRARY = `import { Webhook } from 'standardwebhooks';

const wh = new Webhook(process.env.MELIO_WEBHOOK_SECRET); // whsec_<base64>, exactly as shown in the portal

app.post('/melio/webhooks', express.raw({ type: 'application/json' }), (req, res) => {
  try {
    const event = wh.verify(req.body.toString('utf8'), req.headers); // throws if invalid or older than 5 min
    handle(event);
    res.sendStatus(200);
  } catch {
    res.sendStatus(400);
  }
});`;

const VERIFY_WITHOUT_LIBRARY = `import crypto from 'crypto';

// whsec_<base64>: the HMAC key is the base64 part
const secret = Buffer.from(process.env.MELIO_WEBHOOK_SECRET.replace(/^whsec_/, ''), 'base64');
const TOLERANCE_SECONDS = 300;

function isValidMelioWebhook(rawBody, headers) {
  const id = headers['webhook-id'];
  const timestamp = headers['webhook-timestamp'];
  const signatureHeader = headers['webhook-signature'];
  if (!id || !timestamp || !signatureHeader) return false;

  // Reject old deliveries (replay protection)
  if (Math.abs(Math.floor(Date.now() / 1000) - Number(timestamp)) > TOLERANCE_SECONDS) return false;

  // Sign id.timestamp.rawBody, using the raw body before any JSON parsing
  const expected = crypto.createHmac('sha256', secret).update(\`\${id}.\${timestamp}.\${rawBody}\`).digest();

  // The header may hold several signatures ("v1,abc v1,def"); one match is enough
  return signatureHeader.split(' ').some((entry) => {
    if (!entry.startsWith('v1,')) return false;
    const received = Buffer.from(entry.slice(3), 'base64');
    return received.length === expected.length && crypto.timingSafeEqual(received, expected);
  });
}`;

/** How to verify a delivery against the signing secret, with and without the Standard Webhooks library. */
function VerificationGuide() {
  return (
    <>
      <Container paddingTop="l" paddingBottom="s">
        <Text as="h3" textStyle="heading3Semi">
          Verifying deliveries
        </Text>
      </Container>
      <Container paddingBottom="s">
        <Text textStyle="body3" color="semantic.text.secondary">
          Deliveries follow Standard Webhooks: each carries <Mono>webhook-id</Mono>, <Mono>webhook-timestamp</Mono> and{' '}
          <Mono>webhook-signature</Mono> headers. Check the signature over the raw request body with your signing secret, and reject
          deliveries more than 5 minutes old. Use <Mono>webhook-id</Mono> to ignore retries you’ve already handled.
        </Text>
      </Container>
      <CodeBlock title="With the standardwebhooks library" lang="node" code={VERIFY_WITH_LIBRARY} style={{ marginBottom: 16 }} />
      <CodeBlock title="Without a library" lang="node" code={VERIFY_WITHOUT_LIBRARY} />
    </>
  );
}

type RotateMode = 'overlap' | 'now';

// Fixed width for the secret rows' labels, so the two rows' values line up.
const SECRET_LABEL_WIDTH = 110;
// Shown when a secret's preview couldn't be read.
const SECRET_PLACEHOLDER = 'whsec_••••••••';

function WebhooksView() {
  const app = useApp();
  const env: OnboardEnv = app.isLive ? 'production' : 'staging01';
  const token = async (): Promise<string> => {
    const accessToken = await app.getAccessToken();
    if (!accessToken) throw new Error('Your session has expired. Please sign in again.');
    return accessToken;
  };
  const hook = useAsync(async (signal: AbortSignal) => getWebhook(await token(), env, signal), [env]);
  const eventsQ = useAsync(async (signal: AbortSignal) => listWebhookEvents(await token(), env, signal), [env]);
  const available: WebhookEventName[] = eventsQ.data && eventsQ.data.length ? eventsQ.data : FALLBACK_EVENTS;
  const webhook = hook.data || null;

  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState('');
  const [selected, setSelected] = useState<Set<WebhookEventName>>(new Set());
  const [busy, setBusy] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState(''); // the page's own actions: pause/enable, delete
  const [editorError, setEditorError] = useState(''); // the editor drawer's save, shown only in the drawer
  // The signing secret: the drawer that shows it once (freshly created or rotated), and the rotate dialog.
  const [secretOpen, setSecretOpen] = useState(false);
  const [secret, setSecret] = useState<SigningSecret | null>(null);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotateMode, setRotateMode] = useState<RotateMode>('overlap');
  const [rotating, setRotating] = useState(false);
  const [rotateError, setRotateError] = useState('');

  // One overlap at a time: while the previous secret still signs, only "rotate now" is offered (the API 409s an
  // overlap rotation too), so a secret that leaked mid-overlap can still be cut off.
  const previousSigningUntil = webhook?.signingSecret?.previousSigningUntil;

  // Drop the secret on close, not just hide it: it is shown once and has no reason to stay in memory.
  const closeSecret = () => {
    setSecretOpen(false);
    setSecret(null);
  };

  const openRotate = () => {
    setRotateMode(previousSigningUntil ? 'now' : 'overlap');
    setRotateError('');
    setRotateOpen(true);
  };

  const rotate = async () => {
    setRotating(true);
    setRotateError('');
    try {
      const rotated = await rotateSigningSecret(await token(), env, rotateMode === 'now');
      setRotateOpen(false);
      setSecret(rotated);
      setSecretOpen(true);
      hook.reload();
    } catch (e: any) {
      setRotateError(e?.message || 'Could not rotate the signing secret.');
    } finally {
      setRotating(false);
    }
  };

  const openEditor = () => {
    setUrl(webhook?.url || '');
    setSelected(new Set(webhook?.events || []));
    setEditorError('');
    setEditing(true);
  };

  const toggleEvent = (name: WebhookEventName) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const allSelected = available.length > 0 && available.every((e) => selected.has(e));

  const save = async () => {
    if (!isHttpsUrl(url)) {
      setEditorError('Enter a valid https URL.');
      return;
    }
    if (selected.size === 0) {
      setEditorError('Subscribe to at least one event.');
      return;
    }
    setBusy(true);
    setEditorError('');
    try {
      const saved = await patchWebhook(await token(), env, { url, events: [...selected] });
      setEditing(false);
      // A new endpoint comes with its signing secret, which can't be read again — show it now.
      if (saved.signingSecret && 'secret' in saved.signingSecret) {
        setSecret(saved.signingSecret);
        setSecretOpen(true);
      }
      hook.reload();
    } catch (e: any) {
      setEditorError(e?.message || 'Could not save the webhook endpoint.');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (isActive: boolean) => {
    setToggling(true);
    setError('');
    try {
      await patchWebhook(await token(), env, { isActive });
      hook.reload();
    } catch (e: any) {
      setError(e?.message || 'Could not update the webhook endpoint.');
    } finally {
      setToggling(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    setError('');
    try {
      await deleteWebhook(await token(), env);
      setConfirmingDelete(false);
      hook.reload();
    } catch (e: any) {
      setConfirmingDelete(false);
      setError(e?.message || 'Could not delete the webhook endpoint.');
    } finally {
      setRemoving(false);
    }
  };

  const rowStyle: CSSProperties = {
    marginTop: 16,
    paddingTop: 16,
    borderTop: '1px solid var(--border-static)',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  };

  return (
    <div>
      <PageTitle
        title="Webhooks"
        sub="Register a single https endpoint; we POST events signed with your signing secret (Standard Webhooks) when resources change."
        action={
          hook.loading ? undefined : (
            <Button leftIcon={webhook ? 'edit' : 'add'} onClick={openEditor}>
              {webhook ? 'Edit endpoint' : 'Configure endpoint'}
            </Button>
          )
        }
      />

      {error && (
        <Banner kind="critical" style={{ marginBottom: 16 }}>
          {error}
        </Banner>
      )}

      {hook.loading ? (
        <Loading label="Loading webhook endpoint…" />
      ) : hook.error ? (
        <ErrorBanner error={hook.error} onRetry={hook.reload} />
      ) : !webhook ? (
        <Card padding={32} style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
          No webhook endpoint configured yet. Configure one to start receiving events.
        </Card>
      ) : (
        <>
          <Card padding={22} style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 10,
                  background: webhook.isActive ? 'var(--success-100)' : 'var(--neutral-200)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name="fast" size={22} color={webhook.isActive ? 'var(--success-700)' : 'var(--neutral-600)'} />
              </div>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Mono bold style={{ wordBreak: 'break-all' }}>
                  {webhook.url}
                </Mono>
                <Text textStyle="body4" color="semantic.text.secondary">
                  {webhook.events.length} event{webhook.events.length === 1 ? '' : 's'} subscribed
                </Text>
              </div>
              <Pill kind={webhook.isActive ? 'success' : 'neutral'}>{webhook.isActive ? 'Active' : 'Paused'}</Pill>
            </div>
            <div style={rowStyle}>
              <Text textStyle="body4" color="semantic.text.secondary" style={{ width: SECRET_LABEL_WIDTH }}>
                Signing secret
              </Text>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 8 }}>
                {webhook.signingSecret ? (
                  <>
                    <Mono>{webhook.signingSecret.maskedSecret ?? SECRET_PLACEHOLDER}</Mono>
                    <Pill kind="success">Active</Pill>
                    <Text as="span" textStyle="body4" color="semantic.text.secondary">
                      Created {fmtDateTime(webhook.signingSecret.createdAt)}
                    </Text>
                  </>
                ) : (
                  <Text textStyle="body4" color="semantic.text.secondary">
                    No signing secret, so deliveries can’t be signed. Rotate to create one.
                  </Text>
                )}
              </div>
              <PennyNakedButton
                variant="primary"
                size="medium"
                label={webhook.signingSecret ? 'Rotate' : 'Create'}
                aria-label="Rotate signing secret"
                onClick={openRotate}
              />
            </div>
            {/* after a rotation the previous secret keeps signing until its cutoff, so a delivery carries two signatures */}
            {previousSigningUntil && (
              <div style={{ ...rowStyle, marginTop: 12, paddingTop: 0, borderTop: undefined }}>
                <Text textStyle="body4" color="semantic.text.secondary" style={{ width: SECRET_LABEL_WIDTH }}>
                  Previous secret
                </Text>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 8 }}>
                  <Mono>{webhook.signingSecret?.previousMaskedSecret ?? SECRET_PLACEHOLDER}</Mono>
                  <Pill kind="warning">Rotating out</Pill>
                  <Text as="span" textStyle="body4" color="semantic.text.secondary">
                    Signs until {fmtDateTime(previousSigningUntil)} · {fmtTimeLeft(previousSigningUntil)}
                  </Text>
                </div>
              </div>
            )}
            <div
              style={{
                marginTop: 16,
                paddingTop: 16,
                borderTop: '1px solid var(--border-static)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <Text textStyle="body4" color="semantic.text.secondary" style={{ flex: 1 }}>
                {toggling
                  ? webhook.isActive
                    ? 'Pausing deliveries…'
                    : 'Enabling deliveries…'
                  : webhook.isActive
                    ? 'Deliveries are enabled.'
                    : 'Deliveries are paused.'}
              </Text>
              <Toggle value={webhook.isActive} onChange={(v: boolean) => toggleActive(v)} loading={toggling} disabled={removing} />
              <NakedButton
                color="var(--critical-700)"
                disabled={toggling || removing}
                onClick={() => {
                  if (!toggling && !removing) setConfirmingDelete(true);
                }}
              >
                {removing ? 'Deleting…' : 'Delete'}
              </NakedButton>
            </div>
          </Card>
          <Container paddingBottom="s">
            <Text as="h3" textStyle="heading3Semi">
              Subscribed events
            </Text>
          </Container>
          <Card padding={8}>
            {webhook.events.length === 0 ? (
              <Container paddingX="s" paddingY="s">
                <Text textStyle="body3" color="semantic.text.secondary">
                  No events subscribed.
                </Text>
              </Container>
            ) : (
              webhook.events.map((e, i) => (
                <div
                  key={e}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                    borderTop: i ? '1px solid var(--border-static)' : 'none',
                  }}
                >
                  <Mono style={{ flex: 1 }}>{e}</Mono>
                  <Pill kind="success">on</Pill>
                </div>
              ))
            )}
          </Card>
          <VerificationGuide />
        </>
      )}

      {/* the signing secret, shown once: right after the endpoint is created or the secret rotated */}
      <Drawer
        open={secretOpen}
        onClose={closeSecret}
        title="Webhook signing secret"
        footer={secret ? <Button onClick={closeSecret}>Done</Button> : undefined}
      >
        {secret && (
          <>
            <Banner kind="warning" style={{ marginBottom: 16 }}>
              Copy this secret now. It’s shown only once; if you lose it, rotate the secret to get a new one.
            </Banner>
            {/* Plain text rather than a CodeBlock, whose syntax highlighting would carve up the base64. */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: 16,
                marginBottom: 16,
                borderRadius: 12,
                border: '1px solid var(--border-static)',
              }}
            >
              <Icon name="lock" size={16} color="var(--neutral-600)" />
              <code className="om-mono" style={{ flex: 1, fontSize: 14, wordBreak: 'break-all' }}>
                {secret.secret}
              </code>
              <CopyChip text={secret.secret} dark={false} />
            </div>
            {secret.previousSigningUntil && (
              <Banner kind="informative" style={{ marginBottom: 16 }}>
                Previous secret still signs until <strong>{fmtDateTime(secret.previousSigningUntil)}</strong>. Deploy this secret to your
                webhook verifier before then.
              </Banner>
            )}
            <Text textStyle="body2" color="semantic.text.secondary">
              Your webhook verifier uses this secret to check the <Mono>webhook-signature</Mono> header of each delivery. It can’t call the
              API, so it’s safe to keep it with your webhook receiver rather than with your API keys.
            </Text>
          </>
        )}
      </Drawer>

      {/* rotate the signing secret */}
      <Drawer
        open={rotateOpen}
        onClose={() => setRotateOpen(false)}
        title="Rotate signing secret"
        footer={
          <>
            <Button variant="tertiary" onClick={() => setRotateOpen(false)}>
              Cancel
            </Button>
            <Button variant={rotateMode === 'now' ? 'critical' : 'primary'} onClick={rotate} loading={rotating}>
              Rotate secret
            </Button>
          </>
        }
      >
        {rotateError && (
          <Banner kind="critical" style={{ marginBottom: 16 }}>
            {rotateError}
          </Banner>
        )}
        {previousSigningUntil ? (
          <Banner kind="informative" style={{ marginBottom: 16 }}>
            Your previous secret still signs deliveries until <strong>{fmtDateTime(previousSigningUntil)}</strong>. Until then you can only
            rotate now — the 24-hour overlap is available again after that.
          </Banner>
        ) : (
          <div style={{ marginBottom: 16 }}>
            <Segmented
              value={rotateMode}
              onChange={(v) => setRotateMode(v as RotateMode)}
              options={[
                { value: 'overlap', label: 'Keep the old secret valid for 24h' },
                { value: 'now', label: 'Rotate now' },
              ]}
            />
          </div>
        )}
        {rotateMode === 'overlap' ? (
          <Text textStyle="body2" color="semantic.text.secondary">
            For the next 24 hours every delivery is signed with both the old and the new secret, so your verifier keeps accepting deliveries
            while you deploy the new one. After that, only the new secret signs.
          </Text>
        ) : (
          <Banner kind="critical">
            The current secret stops signing immediately. Deliveries fail verification until your verifier has the new secret. Use this if
            the secret has leaked.
          </Banner>
        )}
      </Drawer>

      {/* delete confirm (danger) */}
      <Drawer
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        title="Delete webhook endpoint"
        footer={
          <>
            <Button variant="tertiary" onClick={() => setConfirmingDelete(false)}>
              Cancel
            </Button>
            <Button variant="critical" loading={removing} onClick={remove}>
              Delete endpoint
            </Button>
          </>
        }
      >
        <Banner kind="critical">
          Deliveries stop, and the endpoint’s signing secret stops working. If you configure an endpoint again, it gets a new signing
          secret.
        </Banner>
      </Drawer>

      <Drawer
        open={editing}
        onClose={() => setEditing(false)}
        title={webhook ? 'Edit webhook endpoint' : 'Configure webhook endpoint'}
        footer={
          <>
            <Button variant="tertiary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button onClick={save} loading={busy}>
              Save endpoint
            </Button>
          </>
        }
      >
        {editorError && (
          <Banner kind="critical" style={{ marginBottom: 16 }}>
            {editorError}
          </Banner>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Field
            label="Endpoint URL"
            required
            helper={webhook ? 'Must be https.' : 'Must be https. Saving creates the signing secret your verifier checks deliveries with.'}
          >
            <Input value={url} onChange={setUrl} placeholder="https://api.partner.co/webhooks/melio" mono />
          </Field>
          <div>
            {/* paddingRight 20 (Card padding 8 + row padding 12) lines this toggle up with the column inside the box */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, paddingRight: 20 }}>
              <Text textStyle="body4Semi" color="semantic.text.secondary" style={{ flex: 1 }}>
                Events {eventsQ.loading ? '· loading…' : ''}
              </Text>
              {available.length > 0 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <Text textStyle="caption1" color="semantic.text.secondary">
                    {allSelected ? 'Clear all' : 'Select all'}
                  </Text>
                  <Toggle value={allSelected} onChange={(v: boolean) => setSelected(v ? new Set(available) : new Set())} />
                </label>
              )}
            </div>
            <Card padding={8}>
              {available.map((e, i) => (
                <div
                  key={e}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 12px',
                    borderTop: i ? '1px solid var(--border-static)' : 'none',
                  }}
                >
                  <Mono style={{ flex: 1 }}>{e}</Mono>
                  <Toggle value={selected.has(e)} onChange={() => toggleEvent(e)} />
                </div>
              ))}
            </Card>
          </div>
        </div>
      </Drawer>
    </div>
  );
}

export { KeysView, LogsView, LogDetail, EventsView, EventDetail, WebhooksView, statusColor };
